import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Divider,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import ForumIcon from '@mui/icons-material/Forum';
import MarkAsUnreadIcon from '@mui/icons-material/MarkAsUnread';
import SendIcon from '@mui/icons-material/Send';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ArchiveIcon from '@mui/icons-material/Archive';
import { STYLE_OPTIONS } from 'src/constants';
import { FILTER_TYPES, FLAG_COLORS } from 'src/constants/chat';

const getOptionIcon = (value) => {
  const iconStyle = { marginRight: '8px', fontSize: '18px' };

  switch (value) {
    case FILTER_TYPES.all:
      return <ForumIcon sx={iconStyle} />;
    case FILTER_TYPES.unread:
      return <MarkAsUnreadIcon sx={iconStyle} />;
    case FILTER_TYPES.sent:
      return <SendIcon sx={iconStyle} />;
    case FILTER_TYPES.scheduled:
      return <ScheduleIcon sx={iconStyle} />;
    case FILTER_TYPES.drafts:
      return <EditNoteIcon sx={iconStyle} />;
    case FILTER_TYPES.flagRed:
      return (
        <Box
          sx={{
            ...iconStyle,
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            backgroundColor: FLAG_COLORS.red,
            flexShrink: 0,
          }}
        />
      );
    case FILTER_TYPES.flagOrange:
      return (
        <Box
          sx={{
            ...iconStyle,
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            backgroundColor: FLAG_COLORS.orange,
            flexShrink: 0,
          }}
        />
      );
    case FILTER_TYPES.flagYellow:
      return (
        <Box
          sx={{
            ...iconStyle,
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            backgroundColor: FLAG_COLORS.yellow,
            flexShrink: 0,
          }}
        />
      );
    case FILTER_TYPES.flagGreen:
      return (
        <Box
          sx={{
            ...iconStyle,
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            backgroundColor: FLAG_COLORS.green,
            flexShrink: 0,
          }}
        />
      );
    case FILTER_TYPES.flagBlue:
      return (
        <Box
          sx={{
            ...iconStyle,
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            backgroundColor: FLAG_COLORS.blue,
            flexShrink: 0,
          }}
        />
      );
    case FILTER_TYPES.archived:
      return <ArchiveIcon sx={iconStyle} />;
    default:
      return null;
  }
};

function ChatsListFilter({
  onChange,
  options,
  value,
  ...props
}) {
  // Find the selected option to display its label
  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <Select
      value={value}
      onChange={onChange}
      size="small"
      color="secondary"
      renderValue={() => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {getOptionIcon(value)}
          <Typography variant="body2" sx={{ fontSize: '14px' }}>
            {selectedOption?.label || 'All Chats'}
          </Typography>
        </Box>
      )}
      MenuProps={{
        PaperProps: {
          style: {
            padding: '14px 16px 8px 16px',
            borderRadius: '12px',
          },
        },
      }}
      sx={{
        height: '28px',
        '& .MuiOutlinedInput-notchedOutline': {
          borderRadius: '14px',
        },
        '& .MuiSelect-select': {
          paddingY: '4px',
          fontSize: '14px',
          color: STYLE_OPTIONS.COLOR_BLUE,
          display: 'flex',
          alignItems: 'center',
        },
      }}
      {...props}
    >
      <Box
        sx={{
          marginBottom: '16px',
        }}
      >
        <Typography variant="h5">Show connects</Typography>
      </Box>
      {options.map((option) => {
        // Check if this is a divider
        if (option.isDivider) {
          return (
            <Divider
              key={option.id}
              sx={{ my: 1 }}
            />
          );
        }

        return (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{
              borderRadius: '4px',
              padding: '8px 6px',
              marginBottom: '10px',
              '&:last-child': {
                marginBottom: 0,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {getOptionIcon(option.value)}
              <Typography variant="body2">
                {option.label}
              </Typography>
            </Box>
          </MenuItem>
        );
      })}
    </Select>
  );
}

ChatsListFilter.propTypes = {
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      isDivider: PropTypes.bool,
    })
  ).isRequired,
  value: PropTypes.string.isRequired
};

export default ChatsListFilter;
